import {
  Box,
  GridLegacy as Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  StaffGeneratorStatusChangeStats,
  StaffGeneratorStatusChangesQuery,
  StaffGeneratorStatusChangesQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_GENERATOR_STATUS_CHANGES } from "hooks/useStaffGeneratorStatusChanges";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeyMetric } from "routes/Staff/Fulfillment/WorkflowOverviewStats/KeyMetric";

type StatusChangeRow =
  StaffGeneratorStatusChangesQuery["staffGeneratorStatusChanges"]["items"][number];

const STATUS_RANK: Record<string, number> = {
  VERY_SMALL_QUANTITY_GENERATOR: 1,
  SMALL_QUANTITY_GENERATOR: 2,
  LARGE_QUANTITY_GENERATOR: 3,
};

function getBgColor({
  escalated,
  downgraded,
  isLight,
}: {
  escalated: boolean;
  downgraded: boolean;
  isLight: boolean;
}): string {
  if (escalated) return isLight ? "rgba(211, 47, 47, 0.06)" : "rgba(244, 67, 54, 0.08)";
  if (downgraded) return isLight ? "rgba(46, 125, 50, 0.06)" : "rgba(76, 175, 80, 0.08)";
  return isLight ? "rgba(237, 108, 2, 0.06)" : "rgba(255, 152, 0, 0.08)";
}

function StatusTransition({
  oldStatus,
  newStatus,
}: {
  oldStatus: string | null | undefined;
  newStatus: string | null | undefined;
}) {
  const theme = useTheme();
  const changed = oldStatus !== newStatus;
  const oldRank = oldStatus ? (STATUS_RANK[oldStatus] ?? 0) : 0;
  const newRank = newStatus ? (STATUS_RANK[newStatus] ?? 0) : 0;
  const escalated = changed && newRank > oldRank;
  const downgraded = changed && newRank < oldRank;

  const oldLabel = oldStatus ? prettyPrintEnumValue(oldStatus) : "—";
  const newLabel = newStatus ? prettyPrintEnumValue(newStatus) : "—";

  if (!changed) {
    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        {newLabel}
      </Typography>
    );
  }

  const bgColor = getBgColor({ escalated, downgraded, isLight: theme.palette.mode === "light" });

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: bgColor,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        maxWidth: "100%",
      }}
    >
      <Typography variant="body2" noWrap>
        {oldLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        →
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap>
        {newLabel}
      </Typography>
    </Box>
  );
}

function defaultStartDate(): DateTime {
  return DateTime.now().minus({ months: 3 }).startOf("month");
}

function formatDateForApi(dt: DateTime | null): string | undefined {
  if (!dt) return undefined;
  return dt.toFormat("yyyy-MM-dd");
}

export const GeneratorStatusChanges = () => {
  useBreadcrumb({ label: "Generator Status Changes - Potential Exceedence" });

  const [startDate, setStartDate] = useState<DateTime | null>(defaultStartDate());
  const [endDate, setEndDate] = useState<DateTime | null>(null);

  const dateVars = useMemo(
    () => ({
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
    }),
    [startDate, endDate]
  );

  const { createUrlFromOmnisearchKeyValues } = useOmnisearchDatagrid({
    persistenceKey: "staffGeneratorStatusChanges",
    columns: [],
  });

  const [stats, setStats] = useState<StaffGeneratorStatusChangeStats | undefined>(undefined);
  const [statsLoading, setStatsLoading] = useState(true);

  const handleQueryStateChange = useCallback(
    ({
      data,
      loading,
    }: {
      data: StaffGeneratorStatusChangesQuery | undefined;
      loading: boolean;
    }) => {
      setStats(data?.staffGeneratorStatusChanges.stats);
      setStatsLoading(loading);
    },
    []
  );

  const handleStartDateChange = useCallback((value: DateTime | null) => {
    setStartDate(value);
  }, []);

  const handleEndDateChange = useCallback((value: DateTime | null) => {
    setEndDate(value);
  }, []);

  return (
    <Stack py={3} spacing={2}>
      {/* Date Range Filter */}
      <Paper elevation={0} sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Period Range:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label="Start"
              value={startDate}
              onChange={handleStartDateChange}
              views={["year", "month"]}
              slotProps={{
                textField: {
                  variant: "standard",
                  size: "small",
                  sx: { width: 150 },
                },
              }}
            />
            <DatePicker
              label="End"
              value={endDate}
              onChange={handleEndDateChange}
              views={["year", "month"]}
              slotProps={{
                textField: {
                  variant: "standard",
                  size: "small",
                  sx: { width: 150 },
                  placeholder: "Current",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={1.5}>
        <Grid item xs={6} md>
          <KeyMetric
            name="Potential Exceedances"
            value={stats?.potentialExceedances}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [],
              keysToKeep: [],
            })}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Facilities Affected"
            value={stats?.facilitiesChanged}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [],
              keysToKeep: [],
            })}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Calculated as LQG"
            value={stats?.exceedancesToLqg}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [["calculatedFederalStatus", "LARGE_QUANTITY_GENERATOR"]],
              keysToKeep: [],
            })}
          />
        </Grid>
        <Grid item xs={6} md>
          <KeyMetric
            name="Calculated as SQG"
            value={stats?.exceedancesToSqg}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [["calculatedFederalStatus", "SMALL_QUANTITY_GENERATOR"]],
              keysToKeep: [],
            })}
          />
        </Grid>
      </Grid>

      {/* Data Grid */}
      <OmnisearchDataGrid<
        StaffGeneratorStatusChangesQuery,
        StaffGeneratorStatusChangesQueryVariables,
        StatusChangeRow
      >
        persistenceKey="staffGeneratorStatusChanges"
        isURLDriven
        dataQueryVariables={dateVars as StaffGeneratorStatusChangesQueryVariables}
        onQueryStateChange={handleQueryStateChange}
        getRowId={(row) => `${row.facilityId}-${row.source}-${row.periodStart}`}
        columns={[
          {
            field: "tenantName",
            headerName: "Organization",
            flex: 0.6,
          },
          {
            field: "facilityName",
            headerName: "Facility",
            flex: 0.6,
            renderCell({ row }) {
              return (
                <Link
                  to={`/o/${row.tenantId}/waste/facilities/${row.facilityId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  {row.facilityName}
                </Link>
              );
            },
          },
          {
            field: "state",
            headerName: "State",
            flex: 0.3,
          },
          {
            field: "source",
            headerName: "Source",
            flex: 0.3,
            filterKeyType: "enum",
            enumValues: ["eManifest", "vendor"],
          },
          {
            field: "periodStart",
            headerName: "Period",
            flex: 0.3,
            valueGetter(_, row) {
              return new Date(row.periodStart).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
              });
            },
          },
          {
            field: "exceedance",
            headerName: "Exceedance",
            flex: 1,
            sortable: false,
            renderCell({ row }) {
              return (
                <StatusTransition
                  oldStatus={row.reportedFederalStatus}
                  newStatus={row.calculatedFederalStatus}
                />
              );
            },
            valueGetter(_, row) {
              const reportedLabel = row.reportedFederalStatus
                ? prettyPrintEnumValue(row.reportedFederalStatus)
                : "—";
              const calcLabel = row.calculatedFederalStatus
                ? prettyPrintEnumValue(row.calculatedFederalStatus)
                : "—";
              return `${reportedLabel} → ${calcLabel}`;
            },
          },
          {
            field: "changeExplanation",
            headerName: "Explanation",
            flex: 1,
            sortable: false,
            renderCell({ row }) {
              return (
                <Tooltip title={row.changeExplanation} placement="top-start">
                  <Typography variant="body2" noWrap sx={{ lineHeight: "inherit" }}>
                    {row.changeExplanation}
                  </Typography>
                </Tooltip>
              );
            },
          },
          {
            field: "updatedAt",
            headerName: "Calculated At",
            flex: 0.4,
            valueGetter(_, row) {
              return new Date(row.updatedAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            },
          },
          {
            field: "stateStatus",
            headerName: "State Status",
            flex: 0.8,
            sortable: false,
            renderCell({ row }) {
              return (
                <StatusTransition oldStatus={row.oldStateStatus} newStatus={row.newStateStatus} />
              );
            },
            valueGetter(_, row) {
              const oldLabel = row.oldStateStatus ? prettyPrintEnumValue(row.oldStateStatus) : "—";
              const newLabel = row.newStateStatus ? prettyPrintEnumValue(row.newStateStatus) : "—";
              return row.oldStateStatus !== row.newStateStatus
                ? `${oldLabel} → ${newLabel}`
                : newLabel;
            },
          },
        ]}
        columnVisibilityModel={{
          stateStatus: false,
        }}
        dataQuery={GET_STAFF_GENERATOR_STATUS_CHANGES}
        getItems={(data) => data.staffGeneratorStatusChanges.items}
        getCount={(data) => data.staffGeneratorStatusChanges.count}
        excludeFilterColumns={[
          "periodStart",
          "changeExplanation",
          "updatedAt",
          "exceedance",
          "stateStatus",
        ]}
      />
    </Stack>
  );
};
